import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Search, Phone, Mail, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const STATUSES = ["new", "contacted", "booked", "archived"];
const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const URG = { emergency: "Emergency", this_week: "This week", flexible: "Flexible" };
const badge = { new: "bg-orange-100 text-orange-800", contacted: "bg-blue-100 text-blue-800", booked: "bg-emerald-100 text-emerald-800", archived: "bg-slate-200 text-slate-700" };

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", { email, password }); onDone(data); }
    catch (x) { setErr(errorText(x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#0F2B5B] px-5">
      <form onSubmit={submit} className="w-full max-w-md rounded-[1.75rem] bg-white p-8 shadow-2xl" data-testid="admin-login-form">
        <Logo />
        <h1 className="font-display mt-8 text-3xl font-extrabold text-[#0F2B5B]">Staff login</h1>
        <p className="mt-1 text-sm text-slate-600">View and manage customer enquiries.</p>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" className="mt-6 w-full rounded-xl border border-slate-300 px-4 py-3.5 outline-none focus:border-[#FF6B00]" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3.5 outline-none focus:border-[#FF6B00]" />
        {err && <p className="mt-3 text-sm font-semibold text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} data-testid="admin-login-submit-button" className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#E05A00] py-4 font-bold uppercase tracking-[0.12em] text-white hover:bg-[#C94F00]">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} Sign in
        </button>
        <a href="/" className="mt-5 block text-center text-sm text-slate-600 hover:text-[#0F2B5B]">← Back to website</a>
      </form>
    </div>
  );
};

const Row = ({ e, onStatus, onDelete }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`admin-enquiry-row-${e.id}`}>
    <td className="px-4 py-4 text-xs text-slate-600 whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</td>
    <td className="px-4 py-4">
      <p className="font-bold text-[#0F2B5B]">{e.name}</p>
      <a href={`tel:${e.phone}`} className="mt-1 flex items-center gap-1 text-sm text-[#1E6FE0]"><Phone className="h-3.5 w-3.5" />{e.phone}</a>
      {e.email && <a href={`mailto:${e.email}`} className="flex items-center gap-1 text-sm text-[#1E6FE0]"><Mail className="h-3.5 w-3.5" />{e.email}</a>}
    </td>
    <td className="px-4 py-4 text-sm">{e.suburb}</td>
    <td className="px-4 py-4 text-sm"><p className="font-semibold">{e.service}</p><p className={e.urgency === "emergency" ? "font-bold text-red-600" : "text-slate-600"}>{URG[e.urgency] ?? e.urgency}</p></td>
    <td className="max-w-xs px-4 py-4 text-sm text-slate-700">
      {e.source === "chat" && <span className="mb-1 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider text-blue-800">Via chat</span>}
      <p>{e.message}</p>
      {Array.isArray(e.photos) && e.photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-photos-${e.id}`}>
          {e.photos.map((p) => (
            <a key={p} href={`${FILES}/${p}`} target="_blank" rel="noreferrer"><img src={`${FILES}/${p}`} alt="Customer photo" className="h-16 w-16 rounded-lg object-cover ring-1 ring-slate-200 hover:ring-[#FF6B00]" /></a>
          ))}
        </div>
      )}
    </td>
    <td className="px-4 py-4">
      <select value={e.status} onChange={(x) => onStatus(e.id, x.target.value)} data-testid={`admin-status-select-${e.id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold capitalize outline-none ${badge[e.status]}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </td>
    <td className="px-4 py-4"><button onClick={() => onDelete(e.id)} aria-label="Delete enquiry" data-testid={`admin-delete-${e.id}`} className="text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
  </tr>
);

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/enquiries"); setItems(Array.isArray(data) ? data : []); }
    catch (x) { toast.error(errorText(x)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { const { data } = await api.patch(`/enquiries/${id}`, { status }); setItems((l) => l.map((i) => (i.id === id ? data : i))); toast.success(`Marked as ${status}`); }
    catch (x) { toast.error(errorText(x)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/enquiries/${id}`); setItems((l) => l.filter((i) => i.id !== id)); }
    catch (x) { toast.error(errorText(x)); }
  };

  const term = q.trim().toLowerCase();
  const shown = items.filter((i) => (filter === "all" || i.status === filter) && (!term || [i.name, i.phone, i.suburb, i.service, i.message].join(" ").toLowerCase().includes(term)));
  const stats = [["Total", items.length], ["New", items.filter((i) => i.status === "new").length], ["Emergency", items.filter((i) => i.urgency === "emergency").length], ["Booked", items.filter((i) => i.status === "booked").length]];

  return (
    <div className="min-h-screen bg-[#F0F5FA]" data-testid="admin-dashboard">
      <header className="flex items-center justify-between bg-white px-5 py-4 shadow-sm lg:px-10">
        <a href="/"><Logo /></a>
        <div className="flex items-center gap-4 text-sm">
          <span className="hidden text-slate-600 sm:block">{user.email}</span>
          <button onClick={onLogout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 font-semibold text-[#0F2B5B] hover:border-[#FF6B00]"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-10 lg:px-10">
        <div className="flex items-end justify-between gap-4">
          <h1 className="font-display text-4xl font-extrabold text-[#0F2B5B]">Enquiries</h1>
          <button onClick={load} data-testid="admin-refresh-button" className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#0F2B5B] shadow-sm"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {stats.map(([l, n]) => <div key={l} className="rounded-2xl bg-white p-5 shadow-sm"><p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">{l}</p><p className="font-display mt-2 text-4xl font-extrabold text-[#0F2B5B]">{n}</p></div>)}
        </div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1"><Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, suburb…" data-testid="admin-search-input" className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-4 outline-none focus:border-[#FF6B00]" /></label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-status-filter" className="rounded-full border border-slate-300 bg-white px-4 py-3 capitalize outline-none">
            <option value="all">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="mt-6 overflow-x-auto rounded-2xl bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-left" data-testid="admin-enquiries-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-600">
              <tr>{["Received", "Customer", "Suburb", "Service", "Message", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
            </thead>
            <tbody>{shown.map((e) => <Row key={e.id} e={e} onStatus={onStatus} onDelete={onDelete} />)}</tbody>
          </table>
          {!loading && shown.length === 0 && <p className="p-10 text-center text-slate-600" data-testid="admin-empty">No enquiries found.</p>}
        </div>
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => { await api.post("/auth/logout").catch(() => null); setUser(false); };

  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#0F2B5B]"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  if (!user) return <Login onDone={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
